import { Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useMemo } from 'react';

import { CenterSpinner } from '@revelio/core';
import {
  type Item,
  SelectionCategories,
  type SelectionListIdNames,
  type TreeItem,
} from '@revelio/iso-utility';

import type { CompanyResultItem } from '../../../../../../shared/types/company-result-item';
import {
  AdditionalReportSelectionCategories,
  type ReportSelectionCategories,
} from '../utils';
import {
  ReportEntityCompanyEditor,
  type ReportEntityCompanyEditorHandle,
  type ReportEntityCompanyEditorProps,
} from './report-entity-company-editor';

interface ReportEntityCompanySelectionsProps
  extends Omit<
    ReportEntityCompanyEditorProps,
    'onSelectionsValue' | 'filterFn' | 'initialSelections'
  > {
  selectionLists: ReportSelectionCategories[];
  initialSelections?: Record<string, TreeItem>;
  onSelections: (selections: Record<string, TreeItem>) => void;
}

const CompanyResultFilters: string[] = [
  AdditionalReportSelectionCategories.COMPANY_REPVUE,
  AdditionalReportSelectionCategories.COMPANY_ORGCHART,
];

const CompanyFilterNoResultsMessages: Record<string, string> = {
  [AdditionalReportSelectionCategories.COMPANY_ORGCHART]:
    'No companies with org chart data available for this search',
  [AdditionalReportSelectionCategories.COMPANY_REPVUE]:
    'No companies with RepVue data available for this search',
};

const toNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Stable empty object — the legacy modal's `initialSelections = {}` default
// allocates a fresh `{}` every render, so without a stable sentinel here the
// useMemo below would invalidate each render and ReportEntityCompanyEditor's
// `setSelections(initialSelections)` effect would reset the user's pick mid-click.
const EMPTY_INITIAL_SELECTIONS: Record<string, CompanyResultItem> = {};

const toCompanyEditorInitialSelections = (
  initialSelections: Record<string, TreeItem> | undefined
): Record<string, CompanyResultItem> => {
  if (!initialSelections || Object.keys(initialSelections).length === 0) {
    return EMPTY_INITIAL_SELECTIONS;
  }

  return Object.entries(initialSelections).reduce<
    Record<string, CompanyResultItem>
  >((acc, [selectionKey, treeItem]) => {
    const item = (treeItem.item ?? {}) as Record<string, unknown>;
    const rcidFromItem = toNumberOrNull(item.rcid ?? item.id);
    const rcidFromKey = toNumberOrNull(
      selectionKey.split('.').pop() ?? selectionKey
    );
    const rcid = rcidFromItem ?? rcidFromKey;
    if (rcid === null) {
      return acc;
    }

    const primaryName = (() => {
      const candidates = [
        item.primary_name,
        item.shortName,
        item.longName,
        item.label,
      ];
      const firstString = candidates.find(
        (candidate): candidate is string =>
          typeof candidate === 'string' && candidate.trim() !== ''
      );
      return firstString ?? String(rcid);
    })();

    const companyId =
      typeof item.company_id === 'string' && item.company_id.trim() !== ''
        ? item.company_id
        : String(rcid);
    const rawEmpCount = toNumberOrNull(item.raw_emp_count) ?? 0;
    const hasSubsidiaries =
      typeof item.has_subsidiaries === 'boolean'
        ? item.has_subsidiaries
        : false;
    const primaryIdentifiers =
      typeof item.primary_identifiers === 'object' &&
      item.primary_identifiers !== null
        ? (item.primary_identifiers as CompanyResultItem['primary_identifiers'])
        : undefined;

    return {
      ...acc,
      [String(rcid)]: {
        primary_name: primaryName,
        company_id: companyId,
        rcid,
        raw_emp_count: rawEmpCount,
        has_subsidiaries: hasSubsidiaries,
        ...(primaryIdentifiers
          ? { primary_identifiers: primaryIdentifiers }
          : {}),
      },
    };
  }, {});
};

/**
 * ReportEntityCompanySelections
 *
 * This component wraps the <ReportEntityCompanyEditor /> to handle filtering
 * company search results for specific datasets, such as RepVue and the
 * OrgChart.
 */
export const ReportEntityCompanySelections = forwardRef<
  ReportEntityCompanyEditorHandle,
  ReportEntityCompanySelectionsProps
>(({ selectionLists, onSelections, ...props }, ref) => {
  const companyFilter = selectionLists
    .filter((selectionList) => CompanyResultFilters.includes(selectionList))
    .pop();
  const companyInitialSelections = useMemo(
    () => toCompanyEditorInitialSelections(props.initialSelections),
    [props.initialSelections]
  );

  const {
    data: filterFn,
    isLoading,
    error,
  } = useQuery<((item: Item) => boolean) | undefined>({
    queryKey: ['companyData', companyFilter],
    queryFn: async () => {
      if (companyFilter) {
        const response = await fetch(
          `/assets/reports/company-filters/${companyFilter}.json`
        );
        if (!response.ok) {
          throw new Error(
            `Error loading company selections: ${response.statusText}`
          );
        }
        const jsonData = await response.json();
        if (!Array.isArray(jsonData)) {
          throw new Error('Expected company filter to be an array');
        }

        const rcidLookup = jsonData.reduce(
          (acc, rcid) => {
            acc[rcid] = true;
            return acc;
          },
          {} as Record<string, boolean>
        );

        return (item: Item) => !!rcidLookup[String(item.rcid ?? item.id)];
      }
      return undefined;
    },
    enabled: !!companyFilter,
  });

  if (isLoading || error) {
    return (
      <Flex minH="60px" justifyContent="center" alignItems="center">
        {isLoading && <CenterSpinner />}
        {error && <Text color="red.600">{error?.message}</Text>}
      </Flex>
    );
  }
  return (
    <ReportEntityCompanyEditor
      {...props}
      initialSelections={companyInitialSelections}
      onSelectionsValue={(items) => {
        // Filtered company selection lists like repvue and orgchart must be mapped back to their original selection list id expected by the report entity
        const mappedSelections: Record<string, TreeItem> = {};
        const selectionListId = companyFilter
          ? companyFilter
          : SelectionCategories.COMPANY;
        Object.keys(items).forEach((key) => {
          const companyItem = items[key];
          // @ts-expect-error noUncheckedIndexedAccess
          const id = `${selectionListId}.${companyItem.rcid}`;
          mappedSelections[id] = {
            id: id,
            item: {
              // @ts-expect-error noUncheckedIndexedAccess
              id: companyItem.rcid,
              // @ts-expect-error noUncheckedIndexedAccess
              shortName: companyItem.primary_name,
              // @ts-expect-error noUncheckedIndexedAccess
              longName: companyItem.primary_name,
              // @ts-expect-error noUncheckedIndexedAccess
              label: companyItem.primary_name,
              // @ts-expect-error noUncheckedIndexedAccess
              ...(companyItem.primary_identifiers?.rics_k50
                ? {
                    rics_k50: String(companyItem?.primary_identifiers.rics_k50),
                    rics_k400: String(
                      companyItem?.primary_identifiers.rics_k400
                    ),
                    rics_k10: String(companyItem?.primary_identifiers.rics_k10),
                  }
                : {}),
              ...companyItem,
            },
            children: [],
            parentId: undefined,
            selectionListId: selectionListId as SelectionListIdNames,
          };
        });
        onSelections?.(mappedSelections);
      }}
      filterFn={filterFn}
      noFilteredResultsMessage={
        companyFilter
          ? CompanyFilterNoResultsMessages[companyFilter]
          : undefined
      }
      ref={ref}
    />
  );
});
